import configs from "./configs";
import dashboardChannelSingleton from "./dashboard-channel-singleton";
import Cookies from "js-cookie";
import { getAuthHeaders, handleInstantAccessUnauthorized } from "./getToken";
import { AUTH_MODE_INSTANT, resolveAuthMode } from "./reauth-hints";
import { normalizeAppearancePreference } from "./appearance-preference";

const qs = new URLSearchParams(location.search);
const AUTH0_TOKEN_STORAGE_KEY = "auth0token";
const AUTH0_TOKEN_COOKIE_KEYS = ["auth0token"];
function isInstantAccessMode() {
  return resolveAuthMode(localStorage) === AUTH_MODE_INSTANT;
}

function getCookieTokenByKey() {
  for (const key of [configs["JWT_COOKIE"], ...AUTH0_TOKEN_COOKIE_KEYS]) {
    if (!key) {
      continue;
    }

    const token = Cookies.get(key);
    if (token) {
      return token;
    }
  }

  return null;
}

function buildBearerAuthHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function getRequestAuthHeaders(url, method, fallbackToken) {
  const headers = await getAuthHeaders(url, method);
  if (headers.Authorization) {
    return headers;
  }
  return buildBearerAuthHeaders(fallbackToken);
}

export function getCookieToken(cookieFallback) {
  // Only Instant Access should rely on localStorage token fallback.
  let token = isInstantAccessMode() ? localStorage.getItem(AUTH0_TOKEN_STORAGE_KEY) : null;

  if (!token && cookieFallback) {
    token = getCookieTokenByKey();
  }

  return token;
}

export function setCookieToken(token) {
  localStorage.setItem(AUTH0_TOKEN_STORAGE_KEY, token);
}

export default async function getAuthUser(
  store,
  cookieFallback = false,
  refetch = false,
  query = "?profile=true",
  options = {}
) {
  if (store?.state.authUser?.name && !refetch) {
    if (window.gtag) {
      window.gtag("set", { user_id: store.state.authUser.id });
    }

    return store.state.authUser;
  }

  const url = configs["CUSTOM_API_URL"] + "/auth0/user" + query;
  let authHeaders = await getRequestAuthHeaders(url, "GET", options.token);

  if (!authHeaders.Authorization && cookieFallback) {
    authHeaders = buildBearerAuthHeaders(getCookieToken(true));
  }

  const code = qs.get("code");

  // if the code exists, remove it from the url
  if (code) {
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + window.location.search.replace(/&?code=[^&]+/, "")
    );
  }

  if (!authHeaders.Authorization && !code) {
    return null;
  }

  if (authHeaders.Authorization) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...authHeaders
        }
      }).catch(() => {
        return null;
      });

      if (!response.ok) {
        handleInstantAccessUnauthorized(response.status);
        return null;
      }

      const data = await response.json();

      const updatedAuthUser = { ...data };
      if (typeof updatedAuthUser.profile !== "object" || updatedAuthUser.profile === null) {
        updatedAuthUser.profile = {};
      }

      delete updatedAuthUser.is_master;

      if (!cookieFallback) {
        const remoteAppearance = data?.preferences?.appearance;
        store.update({
          authUser: updatedAuthUser,
          profile: {
            ...store.state.profile,
            user_color: data.user_color,
            displayName: data.name || store.state.profile.displayName
          },
          activity: {
            ...store.state.activity,
            hasChangedName: !!data.name || store.state.activity.hasChangedName
          },
          ...(remoteAppearance ? { preferences: { appearance: normalizeAppearancePreference(remoteAppearance) } } : {})
        });
      }

      if (window.gtag) {
        window.gtag("set", { user_id: store.state.authUser.id });
      }

      return data;
    } catch (err) {
      return null;
    }
  }

  if (code) {
    try {
      const email = store.state.credentials?.email;
      const dashboardChannel = dashboardChannelSingleton.getInstance(store);

      if (email) {
        const data = await dashboardChannel.loginWithCode(code, email).catch(() => {
          return null;
        });

        if (data) {
          delete data.is_master;

          const remoteAppearance = data?.preferences?.appearance;
          store.update({
            authUser: data,
            profile: {
              ...store.state.profile,
              user_color: data.user_color,
              displayName: data.name || store.state.profile.displayName
            },
            activity: {
              ...store.state.activity,
              hasChangedName: !!data.name || store.state.activity.hasChangedName
            },
            ...(remoteAppearance
              ? { preferences: { appearance: normalizeAppearancePreference(remoteAppearance) } }
              : {})
          });
        }

        return data;
      }
    } catch (err) {
      return null;
    }
  }
}
